// eslint-disable-next-line no-restricted-imports
import { type SortDescriptor } from "@progress/kendo-data-query";
import { useMemo } from "react";

import { BTSelectItem } from "types/apiResponse/apiResponse";

import { BTSelect } from "commonComponents/btWrappers/BTSelect/BTSelect";
import { type GridColumn } from "commonComponents/utilities/Grid/GridContainer.types";

const ASC_SUFFIX = "__asc";
const DESC_SUFFIX = "__desc";
const NO_SORT_VALUE = "__none__";

const noop = () => {};

interface IGridCardSortBarProps {
    columnsList: GridColumn<unknown>[];
    sortingData: SortDescriptor[];
    onSortChange: (newSort: SortDescriptor[]) => void;
    isDisabled?: boolean;
}

const buildItemValue = (columnId: string, dir: "asc" | "desc"): string =>
    `${columnId}${dir === "asc" ? ASC_SUFFIX : DESC_SUFFIX}`;

const parseItemValue = (value: string): SortDescriptor | null => {
    if (value === NO_SORT_VALUE) {
        return null;
    }
    if (value.endsWith(ASC_SUFFIX)) {
        return { field: value.slice(0, -ASC_SUFFIX.length), dir: "asc" };
    }
    if (value.endsWith(DESC_SUFFIX)) {
        return { field: value.slice(0, -DESC_SUFFIX.length), dir: "desc" };
    }
    return null;
};

/**
 * Sort dropdown rendered above the cards. Lists every sortable column twice
 * (asc + desc). Wired to the same `onSortChanged` handler the column header
 * click uses on desktop, so server/state behavior is identical.
 */
export const GridCardSortBar: React.FC<IGridCardSortBarProps> = ({
    columnsList,
    sortingData,
    onSortChange,
    isDisabled,
}) => {
    const sortableColumns = useMemo(
        () => columnsList.filter((col) => col.isSortable),
        [columnsList],
    );

    const sortableColumnIds = useMemo(
        () => new Set(sortableColumns.map((col) => col.id)),
        [sortableColumns],
    );

    const currentValue = useMemo(() => {
        const active = sortingData[0];
        if (
            !active ||
            !active.dir ||
            typeof active.field !== "string" ||
            !sortableColumnIds.has(active.field)
        ) {
            return NO_SORT_VALUE;
        }
        return buildItemValue(active.field, active.dir);
    }, [sortableColumnIds, sortingData]);

    const items = useMemo(() => {
        const result: BTSelectItem[] = [];
        // "No sort selected" is a placeholder shown only when the dropdown
        // can't represent the active sort — either no sort is set, or the
        // active sort is on a column not visible in the card view. Once a
        // representable sort is active, hide the placeholder so users don't
        // pick a dead no-op option.
        if (currentValue === NO_SORT_VALUE) {
            result.push(
                new BTSelectItem({
                    id: NO_SORT_VALUE,
                    name: "No sort selected",
                    value: NO_SORT_VALUE,
                }),
            );
        }
        sortableColumns.forEach((col) => {
            const label = col.title || col.name;
            result.push(
                new BTSelectItem({
                    id: buildItemValue(col.id, "asc"),
                    name: `${label} (Ascending)`,
                    value: buildItemValue(col.id, "asc"),
                }),
            );
            result.push(
                new BTSelectItem({
                    id: buildItemValue(col.id, "desc"),
                    name: `${label} (Descending)`,
                    value: buildItemValue(col.id, "desc"),
                }),
            );
        });
        return result;
    }, [sortableColumns, currentValue]);

    if (sortableColumns.length === 0) {
        return null;
    }

    return (
        <div className="GridCardView-sortBar">
            <label className="GridCardView-sortBarLabel" htmlFor="GridCardView-sort">
                Sort by
            </label>
            <BTSelect
                id="GridCardView-sort"
                data-testid="GridCardView-sort"
                value={currentValue}
                treeData={items}
                disabled={isDisabled}
                onChange={(_field, value) => {
                    if (typeof value !== "string") {
                        return;
                    }
                    const parsed = parseItemValue(value);
                    if (parsed) {
                        onSortChange([parsed]);
                    }
                }}
                onBlur={noop}
            />
        </div>
    );
};
